/**
 * Kiểm xe về bãi ban đêm (anh Tiến 31/10/2026, DEC-670; docs/phases/P12/xe-ve-bai.md).
 * Mốc kiểm 17:30, 21:00, 22:00, 24:00 và 04:00 sáng hôm sau. Mỗi mốc ghi trạng thái từng xe theo số GPS đã lưu;
 * chỉ báo xe chưa về. Ngày 15 và ngày cuối tháng tổng hợp theo tài xế.
 */
import { vnTimeOn } from './gps-status';

const VN_MS = 7 * 3_600_000;

export const YARD_SLOTS = ['17:30', '21:00', '22:00', '24:00', '04:00'] as const;
export type YardSlot = (typeof YARD_SLOTS)[number];
/** Phút tính từ 00:00 ngày của đêm (04:00 = 28:00) */
const SLOT_MINUTES: Record<YardSlot, number> = { '17:30': 17 * 60 + 30, '21:00': 21 * 60, '22:00': 22 * 60, '24:00': 24 * 60, '04:00': 28 * 60 };
export const YARD_SLOT_LABELS: Record<YardSlot, string> = { '17:30': '17:30', '21:00': '21:00', '22:00': '22:00', '24:00': '24:00', '04:00': '04:00 sáng' };

export const YARD_STATES = ['at_yard', 'away', 'no_gps'] as const;
export type YardState = (typeof YARD_STATES)[number];
export const YARD_STATE_LABELS: Record<YardState, string> = { at_yard: 'Ở bãi', away: 'Chưa về', no_gps: 'Mất tín hiệu' };

/** Số GPS cũ hơn mức này (phút) trước mốc kiểm: tin báo ghi "mất tín hiệu, vị trí cuối lúc …" (xe ngoài bãi vẫn là Chưa về) */
export const YARD_GPS_STALE_MINUTES = 60;
/** Vị trí cuối cũ hơn mức này (giờ) thì coi như không có số */
export const YARD_GPS_MAX_AGE_HOURS = 12;
/** Ghi bù mốc đã qua (máy chủ khởi động lại) trong bao nhiêu giờ; chỉ báo chuông khi trễ ≤ YARD_NOTIFY_LATE_MINUTES */
export const YARD_BACKFILL_HOURS = 72;
export const YARD_NOTIFY_LATE_MINUTES = 60;

/** Giờ của mốc kiểm thuộc đêm `night` (yyyy-mm-dd giờ VN) */
export function yardSlotAt(night: string, slot: YardSlot): Date {
  const m = SLOT_MINUTES[slot];
  return vnTimeOn(night, `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
}

/** Đêm (ngày VN) chứa thời điểm t: từ 12:00 hôm đó tới 12:00 hôm sau */
export function yardNightOf(t: Date): string {
  return new Date(t.getTime() + VN_MS - 12 * 3_600_000).toISOString().slice(0, 10);
}

const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const lastDayOfMonth = (d: string) => {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
};

export interface YardPeriod {
  /** Ngày gửi tổng hợp (ngày 15 hoặc ngày cuối tháng) */
  reportOn: string;
  /** Đêm đầu / đêm cuối của kỳ (gồm cả hai) */
  from: string;
  to: string;
  label: string;
}

const dm = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

/**
 * Kỳ tổng hợp gửi vào ngày `day` (null nếu không phải ngày 15 / ngày cuối tháng).
 * Gửi 08:00, khi đêm trước đã kiểm xong mốc 04:00, nên mỗi đêm vào đúng 1 kỳ:
 * - ngày 15: đêm ngày cuối tháng trước → đêm 14
 * - ngày cuối tháng: đêm 15 → đêm áp chót
 */
export function yardPeriodReportedOn(day: string): YardPeriod | null {
  const dd = Number(day.slice(8, 10));
  let from: string;
  if (dd === 15) from = addDays(`${day.slice(0, 8)}01`, -1);
  else if (day === lastDayOfMonth(day)) from = `${day.slice(0, 8)}15`;
  else return null;
  const to = addDays(day, -1);
  return { reportOn: day, from, to, label: `đêm ${dm(from)} → ${dm(to)}` };
}

/** Kỳ chứa đêm `night` (để màn hình mở đúng kỳ đang chạy) */
export function yardPeriodOfNight(night: string): YardPeriod {
  const dd = Number(night.slice(8, 10));
  const last = lastDayOfMonth(night);
  const reportOn = night === last ? `${addDays(night, 1).slice(0, 8)}15` : dd >= 15 ? last : `${night.slice(0, 8)}15`;
  return yardPeriodReportedOn(reportOn)!;
}

/** Hiển thị khoảng cách: "350 m" / "12,3 km" */
export function fmtDistanceVN(m: number | null): string {
  if (m === null) return '';
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} km`;
}

/** Một dòng kiểm (API trả về) */
export interface YardCheckRow {
  nightDate: string;
  slot: YardSlot;
  checkedAt: string;
  vehicleId: number;
  plateNo: string;
  driverId: number | null;
  driverName: string | null;
  state: YardState;
  gpsAt: string | null;
  gpsStatus: string | null;
  speedKmh: number | null;
  lat: number | null;
  lng: number | null;
  distanceM: number | null;
  homeName: string | null;
  arrivedAt: string | null;
  excused: boolean;
  /** Dòng đã được đọc lại từ GPS (DEC-840); null = ghi đúng lúc mốc kiểm */
  rebuiltAt?: string | null;
}

/** Đọc lại sổ từ GPS: tối đa bao nhiêu đêm mỗi lần (DEC-840) */
export const YARD_REBUILD_MAX_NIGHTS = 62;

/** Kết quả đọc lại sổ Xe về bãi từ GPS (xem trước hoặc đã ghi) */
export interface YardRebuildChange {
  nightDate: string;
  slot: YardSlot;
  plateNo: string;
  driverName: string | null;
  /** null = trước đây chưa có dòng sổ */
  oldState: YardState | null;
  newState: YardState;
  oldDistanceM: number | null;
  newDistanceM: number | null;
  oldArrivedAt: string | null;
  newArrivedAt: string | null;
}
export interface YardRebuildResult {
  from: string;
  to: string;
  applied: boolean;
  /** Số mốc (đêm × mốc) đã tính */
  slots: number;
  rows: number;
  added: number;
  changed: number;
  unchanged: number;
  /** Đổi từ Chưa về / Mất tín hiệu sang Ở bãi */
  toYard: number;
  /** Đổi từ Ở bãi sang Chưa về */
  toAway: number;
  /** Dòng không có số GPS nào trong 12 giờ trước mốc (để nguyên Mất tín hiệu) */
  noGps: number;
  /** Đêm có xe thiếu số GPS (yyyy-mm-dd biển số) */
  noGpsNights: string[];
  changes: YardRebuildChange[];
  /** Số dòng đổi nhiều hơn danh sách trả về */
  moreChanges: number;
}

/** Tổng hợp 1 tài xế trong kỳ */
export interface YardDriverSummary {
  driverId: number | null;
  driverName: string;
  plates: string[];
  /** Số đêm có kiểm lúc 22:00 khi tài xế giữ xe */
  nights: number;
  /** Số đêm chưa về (không có lý do) theo từng mốc */
  away: Record<YardSlot, number>;
  /** Đêm không về bãi cả đêm (04:00 vẫn ngoài, không có lý do) */
  allNight: number;
  /** Đêm chưa về nhưng có chuyến qua đêm / chuyến tỉnh */
  excused: number;
  /** Đêm có mốc (từ 21:00) không có số GPS 12 giờ qua: không tính lỗi tài xế, cần kiểm thiết bị */
  noGps: number;
  /** Giờ về trễ nhất (sau 17:30) và giờ về trung bình, phút tính từ 00:00 đêm đó (có thể > 1440) */
  latestArrivalMin: number | null;
  avgArrivalMin: number | null;
  /** Các đêm có lỗi: ngày + mốc đầu tiên chưa về */
  lateNights: { night: string; plateNo: string; firstAway: YardSlot; arrivedAt: string | null }[];
}

/** "21:45", "00:30 (+1)" từ phút tính từ 00:00 của đêm */
export function fmtNightMinutes(min: number | null): string {
  if (min === null) return '';
  const m = Math.round(min);
  const h = Math.floor(m / 60);
  const s = `${String(h % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
  return h >= 24 ? `${s} (+1)` : s;
}

/** Phút tính từ 00:00 ngày `night` (giờ VN) */
export function minutesIntoNight(night: string, t: Date | string): number {
  return (new Date(t).getTime() - vnTimeOn(night, '00:00').getTime()) / 60_000;
}
